import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { formatTime } from 'shared/helpers/formatTime';
import { resolveAllocationAccentColor } from 'dashboard/routes/dashboard/planning/events/useEventStageColors';
import { isFullDay } from './scheduleUtils';

export function useAllocationDisplay() {
  const store = useStore();
  const { t } = useI18n();

  const getEventForAllocation = allocation => {
    if (!allocation?.event_id) return null;
    return store.getters['planningEvents/getEventById'](allocation.event_id);
  };

  const getEventLabelColor = allocation =>
    allocation.event_label_color ||
    getEventForAllocation(allocation)?.event_label?.color ||
    null;

  const getEventAccentColor = allocation =>
    resolveAllocationAccentColor(allocation, getEventForAllocation(allocation));

  const getDisplayTitle = allocation =>
    getEventForAllocation(allocation)?.name ||
    allocation.event_name ||
    allocation.title ||
    t('PLANNING.SCHEDULER.UNTITLED_SLOT');

  const formatTimeRange = allocation => {
    if (isFullDay(allocation)) return t('PLANNING.SCHEDULER.ALL_DAY');
    const start = new Date(allocation.start_at);
    const end = new Date(allocation.end_at);
    return `${formatTime(start)} - ${formatTime(end)}`;
  };

  const getHierarchyBlockerLabel = allocation =>
    `${getDisplayTitle(allocation)} ${formatTimeRange(allocation)}`;

  const showBlockerTitleIfTruncated = event => {
    const el = event.currentTarget;
    el.title = el.scrollWidth > el.clientWidth ? el.textContent.trim() : '';
  };

  const getAllocationStyle = () => ({
    // Opaque underlay so calendar gridlines don't show through the tint
    background:
      'linear-gradient(rgba(253, 176, 34, 0.25), rgba(253, 176, 34, 0.25)), var(--color-n-solid-1, #ffffff)',
    color: 'var(--color-n-slate-12, #111827)',
  });

  const getLineItemStyle = () => ({});

  const getEventStatus = allocation => {
    let status = allocation.metadata?.event_status;
    if (!status && allocation.event_id) {
      const event = store.getters['planningEvents/getEventById'](
        allocation.event_id
      );
      status = event?.system_stage;
    }
    return status;
  };

  const isTentativeEventStatus = allocation => {
    const status = getEventStatus(allocation);
    return status === 'draft' || status === 'inquiry';
  };

  const isLostEventStatus = allocation => getEventStatus(allocation) === 'lost';

  const releasesBookedSpace = allocation => {
    const status = getEventStatus(allocation);
    return status === 'lost' || status === 'archived';
  };

  const getStatusClass = allocation => {
    if (allocation.status === 'invalid' || allocation.status === 'cancelled') {
      return 'status-invalid';
    }
    if (isTentativeEventStatus(allocation)) {
      return 'status-tentative';
    }
    return '';
  };

  return {
    getEventForAllocation,
    getEventLabelColor,
    getEventAccentColor,
    getDisplayTitle,
    formatTimeRange,
    getHierarchyBlockerLabel,
    showBlockerTitleIfTruncated,
    getAllocationStyle,
    getLineItemStyle,
    getStatusClass,
    isLostEventStatus,
    releasesBookedSpace,
  };
}
